import APIClient, { DevicePublishedContent } from '@raydiant/api-client-js';
import { useState, useEffect, useMemo, useCallback } from 'react';
import Pubnub from 'pubnub';

export default function usePublishedContent(
  apiClient: APIClient,
  pubnubClient: Pubnub,
  logger: Logger,
): [DevicePublishedContent | null, Error | null] {
  const [result, setResult] = useState<DevicePublishedContent | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const makeRequest = useCallback(async () => {
    try {
      setResult(await apiClient.getDevicePublishedContent());
    } catch (err) {
      if (err instanceof Error) {
        logger.error(
          `Failed to fetch published content with  '${err.message}'`,
        );
        setError(err);
      } else {
        logger.error(
          `Failed to fetch published content with an unknown error: ${err}`,
        );
        setError(new Error('Unknown error'));
      }
    }
  }, [apiClient, logger]);

  // We need to memoize the { message: <handler> } object because pubnub removes
  // listeners by reference of the object rather than the callback.
  const handleMessage = useMemo(
    () => ({
      message: (event: Pubnub.MessageEvent) => {
        if (
          event.message &&
          typeof event.message === 'object' &&
          event.message.published_at
        ) {
          logger.info(`Received 'published_at' message from pubnub`);
          makeRequest();
        }
      },
    }),
    [makeRequest, logger],
  );

  // Initial fetch published content.
  useEffect(() => {
    makeRequest();
  }, [makeRequest]);

  // Re-fetch published content on publish pubnub message.
  useEffect(() => {
    pubnubClient.addListener(handleMessage);

    return () => {
      pubnubClient.removeListener(handleMessage);
    };
  }, [pubnubClient, handleMessage]);

  return [result, error];
}
